/**
 * Winkel Status - Determines if a store is currently open based on opening hours
 * Handles format like: "08u30 - 10u00 & 13u00 - 17u30" or "Gesloten"
 */
(function () {
  'use strict';

  /**
   * Parse time string like "08u30" to minutes since midnight
   * @param {string} timeStr - Time string in format "HHuMM"
   * @returns {number|null} - Minutes since midnight or null if invalid
   */
  function parseTime(timeStr) {
    if (!timeStr) return null;

    // Clean up the string
    const cleaned = timeStr.trim().toLowerCase();

    // Match format like "08u30" or "8u30"
    const match = cleaned.match(/^(\d{1,2})u(\d{2})$/);
    if (!match) return null;

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

    return hours * 60 + minutes;
  }

  /**
   * Check if current time falls within a time range
   * @param {number} currentMinutes - Current time in minutes since midnight
   * @param {string} rangeStr - Time range like "08u30 - 17u30"
   * @returns {object} - { isOpen: boolean, closingTime: string|null, openTime: number|null, openTimeStr: string|null }
   */
  function checkTimeRange(currentMinutes, rangeStr) {
    const parts = rangeStr.split('-').map((s) => s.trim());
    if (parts.length !== 2) return { isOpen: false, closingTime: null, openTime: null, openTimeStr: null };

    const openTime = parseTime(parts[0]);
    const closeTime = parseTime(parts[1]);

    if (openTime === null || closeTime === null) return { isOpen: false, closingTime: null, openTime: null, openTimeStr: null };

    if (currentMinutes >= openTime && currentMinutes < closeTime) {
      return { isOpen: true, closingTime: parts[1], openTime: openTime, openTimeStr: parts[0] };
    }

    return { isOpen: false, closingTime: null, openTime: openTime, openTimeStr: parts[0] };
  }

  /**
   * Determine store status based on opening hours string
   * @param {string} hoursStr - Opening hours like "08u30 - 10u00 & 13u00 - 17u30"
   * @returns {object} - { isOpen: boolean, closingTime: string|null, nextOpeningTime: string|null }
   */
  function getStoreStatus(hoursStr) {
    if (!hoursStr || hoursStr.toLowerCase() === 'gesloten') {
      return { isOpen: false, closingTime: null, nextOpeningTime: null };
    }

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    // Split by "&" for multiple time periods (e.g., morning and afternoon)
    const periods = hoursStr.split('&').map((s) => s.trim());

    let nextOpeningTime = null;
    let nextOpeningMinutes = null;

    for (const period of periods) {
      const result = checkTimeRange(currentMinutes, period);

      if (result.isOpen) {
        return { isOpen: true, closingTime: result.closingTime, nextOpeningTime: null };
      }

      // Track the next opening time for "opens at" message
      if (result.openTime !== null && result.openTime > currentMinutes) {
        if (nextOpeningMinutes === null || result.openTime < nextOpeningMinutes) {
          nextOpeningMinutes = result.openTime;
          nextOpeningTime = result.openTimeStr;
        }
      }
    }

    return { isOpen: false, closingTime: null, nextOpeningTime: nextOpeningTime };
  }

  /**
   * Update all store status elements on the page
   */
  function updateStoreStatusElements() {
    const statusElements = document.querySelectorAll('.js-winkel-status');

    statusElements.forEach((element) => {
      const hoursStr = element.dataset.hours;
      const openText = element.dataset.openText || 'Nu open tot __TIME__';
      const closedText = element.dataset.closedText || 'Gesloten';
      const opensAtText = element.dataset.opensAtText || 'Nu gesloten, opent terug om __TIME__';

      const status = getStoreStatus(hoursStr);

      const textElement = element.querySelector('.winkel-status-text');

      if (status.isOpen) {
        element.classList.remove('winkel-status--closed');
        element.classList.add('winkel-status--open');
        if (textElement) {
          textElement.textContent = openText.replace('__TIME__', status.closingTime);
        }
      } else {
        element.classList.remove('winkel-status--open');
        element.classList.add('winkel-status--closed');
        if (textElement) {
          // If there's a next opening time today, show "opens at" message
          if (status.nextOpeningTime) {
            textElement.textContent = opensAtText.replace('__TIME__', status.nextOpeningTime);
          } else {
            textElement.textContent = closedText;
          }
        }
      }
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateStoreStatusElements);
  } else {
    updateStoreStatusElements();
  }

  // Optionally update every minute to keep status current
  setInterval(updateStoreStatusElements, 60000);
})();
